import { useState } from "react";
import TrainingTab from "./TrainingTab";
import InferenceTab from "./InferenceTab";
import MonitorTab from "./MonitorTab";
import LoginPage from "./LoginPage";
import ResultsTab from "./Resultstab";
import { useAuth } from "./AuthContext";

const API_BASE = "https://mng.sensorplus.gwc.dm-airtech.com";

const C = {
  bg: "#0f1117", surface: "#181c25", surfaceAlt: "#1e2330", border: "#2a3045",
  text: "#e8ecf4", textSub: "#7a8499", accent: "#4f8ef7", success: "#3ecf8e",
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: ${C.bg}; color: ${C.text}; font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.5; }
  input, select, textarea {
    background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text};
    border-radius: 6px; padding: 7px 10px; font-size: 13px; font-family: inherit;
    outline: none; transition: border-color 0.15s;
  }
  input:focus, select:focus { border-color: ${C.accent}; }
  button {
    background: ${C.surfaceAlt}; border: 1px solid ${C.border}; color: ${C.text};
    border-radius: 6px; padding: 8px 14px; font-size: 13px; font-family: inherit;
    cursor: pointer; transition: all 0.15s; display: inline-flex; align-items: center; gap: 6px;
  }
  button:hover:not(:disabled) { border-color: ${C.accent}; color: ${C.accent}; }
  button.primary { background: ${C.accent}; border-color: ${C.accent}; color: #fff; }
  button.primary:hover:not(:disabled) { opacity: 0.88; color: #fff; }
  button.success { background: ${C.success}; border-color: ${C.success}; color: #fff; }
  button:disabled { opacity: 0.4; cursor: not-allowed; }
  table { border-collapse: collapse; width: 100%; }
  th { font-size: 11px; font-weight: 500; color: ${C.textSub}; text-align: left; padding: 6px 8px; border-bottom: 1px solid ${C.border}; white-space: nowrap; }
  td { padding: 7px 8px; border-bottom: 1px solid ${C.border}; font-size: 12px; }
  details summary { cursor: pointer; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
`;

const TABS = [
  { id: "training",  label: "Training data", icon: "🎓" },
  { id: "inference", label: "Inference",      icon: "⚡" },
  { id: "monitor",   label: "Live monitor",   icon: "📡" },
  { id: "results",   label: "Results",        icon: "📊" },
];

function TabBar({ active, onChange }) {
  return (
    <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}` }}>
      {TABS.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          background: "none", border: "none",
          borderBottom: active === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
          borderRadius: 0, color: active === t.id ? C.accent : C.textSub,
          padding: "8px 14px", fontWeight: active === t.id ? 600 : 400,
          marginBottom: -1, gap: 6,
        }}>
          <span>{t.icon}</span> {t.label}
        </button>
      ))}
    </div>
  );
}

export default function App() {
  const { isAuthenticated, user, apiKey, login, logout } = useAuth();
  const [tab, setTab] = useState("training");

  // ─── Not logged in → show login gate ───────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <>
        <style>{styles}</style>
        <LoginPage onSuccess={login} />
      </>
    );
  }

  // ─── Logged in → show the real app ─────────────────────────────────────────
  return (
    <>
      <style>{styles}</style>
      <div style={{ maxWidth: 820, margin: "0 auto", padding: "24px 20px" }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 600, letterSpacing: -0.3 }}>
              SensorPlus <span style={{ color: C.accent }}>Pipeline</span>
            </h1>
            <p style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>
              DM AirTech · {API_BASE.replace("https://", "")}
            </p>
          </div>

          {/* Right: status + user + logout */}
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: C.success, display: "inline-block" }} />
            <span style={{ fontSize: 11, color: C.textSub }}>API live</span>

            {user && (
              <span style={{
                fontSize: 11, color: C.textSub,
                background: C.surfaceAlt, border: `1px solid ${C.border}`,
                borderRadius: 6, padding: "3px 8px",
              }}>
                {user.username}
                <span style={{ color: C.accent, marginLeft: 4 }}>({user.role})</span>
              </span>
            )}

            <button onClick={logout} style={{ fontSize: 11, padding: "4px 10px" }}>
              Sign out
            </button>
          </div>
        </div>

        <TabBar active={tab} onChange={setTab} />

        {/* Pass apiKey down so tabs can use it for authenticated requests */}
        {tab === "training"  && <TrainingTab  apiKey={apiKey} />}
        {tab === "inference" && <InferenceTab apiKey={apiKey} />}
        {tab === "monitor"   && <MonitorTab   apiKey={apiKey} />}
        {tab === "results"   && <ResultsTab   apiKey={apiKey} />}
      </div>
    </>
  );
}